import type { ComponentPropsWithoutRef, FunctionComponent } from 'react'
import React from 'react'

import classnames from 'classnames'

import type { PromoImgAR } from '../Cards/PromoCard'
import { PromoCard } from '../Cards/PromoCard'
import { TypographyV2 } from '../Seatbelt2/Foundations/Typography'
import styles from './Callout.module.scss'

export const Callout: FunctionComponent<Props> = ({
  className,
  byline,
  cards = [],
  description,
  imageAspectRatio,
  title,
  ...rest
}) => {
  return cards.length > 1 ? (
    <section
      className={classnames(className)}
      {...rest}
      data-testid="callout-wrapper"
    >
      {byline && (
        <TypographyV2
          tag="p"
          textStyle="sb-text-interface-body-small-strong"
          className={classnames(styles.calloutByline, 'sb-margin-bottom-1')}
        >
          {byline}
        </TypographyV2>
      )}

      {title && (
        <TypographyV2
          tag="h2"
          data-testid="callout-title"
          textStyle="sb-text-heading-large"
          className={classnames(styles.calloutTitle, 'sb-padding-bottom-3')}
        >
          {title}
        </TypographyV2>
      )}

      {description && (
        <TypographyV2
          tag="p"
          textStyle="sb-text-interface-body-default-regular"
          className={classnames(
            styles.calloutDescription,
            'sb-padding-bottom-3'
          )}
        >
          {description}
        </TypographyV2>
      )}

      <div
        className={classnames(styles.calloutCardWrapper, 'sb-padding-bottom-3')}
      >
        {cards
          .map(
            ({ title, image, strapline, href }, i) =>
              href &&
              image && (
                <PromoCard
                  key={`title-${i}`}
                  className={styles.calloutCard}
                  {...{ href, image, imageAspectRatio, strapline, title }}
                />
              )
          )
          .slice(0, 3)}
      </div>
    </section>
  ) : null
}

///////// IMPLEMENTATION /////////

type Props = {
  /**
   * Class name to be added to component for extra styling purposes
   */
  className?: string
  /**
   * Text to be added as byline
   */
  byline?: string
  /**
   * Text to be added as a description
   */
  description?: string
  /**
   * Image aspect ratio
   */
  imageAspectRatio?: PromoImgAR
  /**
   * Text to be added as title
   */
  title?: string
  /**
   * Cards - min 2 and max 3
   */
  cards: Array<{
    href?: string
    image?: {
      alt: string
      src: string
      sources?: Array<{
        srcset: string
        media?: string
        sizes?: string
        type?: string
      }>
    }
    strapline?: string
    title?: string
  }>
} & ComponentPropsWithoutRef<'section'>
